import { useCallback, useEffect, useRef, useState } from "react";
import PdfOverlay from "./PdfOverlay";
import {
  normalizeIncomingBboxes,
  buildRedactPayload,
  groupSospetti,
  statoDaSalvare,
  statoDaRipristinare,
  stesseAree,
} from "./bboxUtils";
import { loadPdfJs, loadPdfDocumentFromBytes } from "../pdfjsLoader";
import { computeFitScaleForPage, shouldFitRasterInViewer } from "../pdfFitUtils";
import {
  fetchEditorPayload,
  redactPdf,
} from "./editorApi";
import { nomeAnonimizzato } from "./redazioneBatch";
import { F as EDITOR_F } from "../theme/tokens";

const EDITOR_C = {
  border: "#def0f9",
  textTitle: "#182E4F",
  textNum: "#5D6D84",
  smartBlue: "#009CDD",
  smartBlue20: "#EAF8FF",
  // Terzo stato (arbitro): stesso ambra del contrassegno sulle scatole.
  sospetto: "#D97706",
  sospettoBg: "#FFF7ED",
  sospettoBorder: "#FED7AA",
  // Segnalazione gia' guardata: verde, come "pronto" nella barra laterale.
  controllata: "#1F6B3A",
  btnBg: "#0A3458",
  white: "#ffffff",
  radius: "12px",
};

async function loadPdfDocument(pdfjs, data) {
  return loadPdfDocumentFromBytes(pdfjs, data);
}

function EditorBusyOverlay({ title, subtitle, detail }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 12,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 20,
        background: "rgba(10, 34, 56, 0.88)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        borderRadius: EDITOR_C.radius,
        padding: "32px 24px",
      }}
    >
      <svg
        width="64"
        height="64"
        viewBox="0 0 80 80"
        aria-hidden="true"
        style={{ animation: "obscura-editor-spin 2s linear infinite" }}
      >
        <circle cx="40" cy="40" r="30" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
        <path d="M40 10 A30 30 0 0 1 70 40" fill="none" stroke="#009CDD" strokeWidth="3" strokeLinecap="round" />
        <path d="M40 10 C40 25,25 40,40 40 C55 40,40 55,40 70" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2" />
      </svg>
      <div style={{ width: 220, maxWidth: "100%", height: 4, background: "rgba(255,255,255,0.12)", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ height: "100%", background: "linear-gradient(90deg, #009CDD, #59C9F4)", borderRadius: 999, animation: "obscura-editor-progress 2.2s ease-in-out infinite" }} />
      </div>
      <div style={{ textAlign: "center", maxWidth: 320 }}>
        <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#fff", fontFamily: EDITOR_F.display, lineHeight: 1.4 }}>
          {title}
        </p>
        {subtitle && (
          <p style={{ margin: "8px 0 0", fontSize: 13, color: "rgba(255,255,255,0.72)", fontFamily: EDITOR_F.body, lineHeight: 1.5 }}>
            {subtitle}
          </p>
        )}
        {detail && (
          <p style={{ margin: "10px 0 0", fontSize: 12, color: "rgba(0,156,221,0.95)", fontFamily: EDITOR_F.body, fontWeight: 600 }}>
            {detail}
          </p>
        )}
      </div>
    </div>
  );
}

function EditorIcon({ children }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, flexShrink: 0 }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
      </svg>
    </span>
  );
}

function EditorToolButton({ active, disabled, onClick, icon, label, hint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 14px",
        borderRadius: 10,
        border: `1px solid ${active ? EDITOR_C.smartBlue : EDITOR_C.border}`,
        background: active ? EDITOR_C.smartBlue20 : EDITOR_C.white,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        textAlign: "left",
        fontFamily: EDITOR_F.body,
      }}
    >
      <span style={{ color: active ? EDITOR_C.smartBlue : EDITOR_C.textNum }}>{icon}</span>
      <span>
        <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: active ? EDITOR_C.smartBlue : EDITOR_C.textTitle, lineHeight: 1.2 }}>{label}</span>
        <span style={{ display: "block", fontSize: 10, color: EDITOR_C.textNum, marginTop: 3, lineHeight: 1.2 }}>{hint}</span>
      </span>
    </button>
  );
}

function editorControlBtnStyle(disabled) {
  return {
    minWidth: 30,
    height: 30,
    padding: "0 8px",
    borderRadius: 6,
    border: `1px solid ${EDITOR_C.border}`,
    background: EDITOR_C.white,
    color: EDITOR_C.textTitle,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: EDITOR_F.body,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
  };
}

/**
 * I tre pulsanti di una segnalazione.
 *
 * "Oscura" e "Lascia" hanno lo stesso peso visivo di proposito: nessuno dei
 * due e' l'azione consigliata, perche' il sistema non sa quale sia la
 * risposta giusta - se lo sapesse non l'avrebbe segnalata. Dare a uno dei due
 * l'aspetto del pulsante primario sarebbe un suggerimento travestito da stile.
 */
function azioneStyle(disabled, tipo) {
  const base = {
    flex: 1,
    height: 30,
    padding: "0 8px",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: EDITOR_F.body,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
  };
  if (tipo === "vai") {
    return { ...base, flex: "0 0 auto", border: `1px solid ${EDITOR_C.sospetto}`, background: EDITOR_C.white, color: "#9A3412" };
  }
  return { ...base, border: `1px solid ${EDITOR_C.border}`, background: EDITOR_C.white, color: EDITOR_C.textTitle };
}

function EditorControlGroup({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {children}
    </div>
  );
}

/**
 * @param {{
 *   jobId: string,
 *   fileIndex: number,
 *   onRedactComplete?: (bytes: ArrayBuffer, filename: string) => void,
 * }} props
 */
export default function PdfEditor({
  jobId, fileIndex, sessionId = null, onRedactComplete, onSospettiChange, statoSalvato, onStatoChange,
  // Documenti NON aperti con modifiche da applicare: se ce ne sono,
  // "Anonimizza" diventa "Anonimizza tutti" e, finito il proprio, passa la
  // mano alla pagina con onRedactAll. bloccato = la pagina sta lavorando
  // sugli altri documenti.
  altriDaApplicare = 0, onRedactAll, bloccato = false,
}) {
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const scrollRef = useRef(null);
  const loadKeyRef = useRef(null);
  const statoSalvatoRef = useRef(statoSalvato);
  statoSalvatoRef.current = statoSalvato;
  // Le aree come le ha date il server all'apertura: il confronto con quelle a
  // schermo dice se c'e' qualcosa da applicare.
  const riferimentoRef = useRef([]);

  const [pdf, setPdf] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [scale, setScale] = useState(1.25);
  const [viewport, setViewport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redacting, setRedacting] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [bboxes, setBboxes] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Segnalazioni gia' guardate dall'utente. Vive qui e non nelle bbox perche'
  // non e' una proprieta' del documento: e' lo stato della revisione, e non
  // deve finire nel payload di /redact.
  const [confermati, setConfermati] = useState(() => new Set());
  const [tool, setTool] = useState("bbox");
  const [rendering, setRendering] = useState(false);
  const [pendingFitScale, setPendingFitScale] = useState(false);

  const busy = loading || redacting || rendering || bloccato;

  // Vero solo dopo che un caricamento e' andato a buon fine. Prima di allora i
  // riquadri sono [] perche' non e' ancora arrivato niente, non perche'
  // l'utente li abbia tolti: salvarli in quello stato cancellerebbe la
  // revisione vera.
  const caricatoRef = useRef(false);

  const loadFromApi = useCallback(async () => {
    const key = `${jobId}:${fileIndex}`;
    if (loadKeyRef.current === key) return;
    loadKeyRef.current = key;

    caricatoRef.current = false;
    setLoading(true);
    setError(null);
    setPdf(null);
    setViewport(null);
    setPageNum(1);
    setSelectedIds(new Set());
    setConfermati(new Set());
    setScale(1.25);
    setPendingFitScale(false);

    try {
      const payload = await fetchEditorPayload(jobId, fileIndex, sessionId);
      const pdfjs = await loadPdfJs();
      const data = payload.pdfBytes.slice(0);
      const doc = await loadPdfDocument(pdfjs, data);
      const normalized = await normalizeIncomingBboxes(doc, payload.bboxes);
      const needsFit = await shouldFitRasterInViewer(doc, payload.originalFilename || "");
      setPdfBytes(data);
      setPdf(doc);
      setFileName(payload.pdfFilename || payload.originalFilename || "document.pdf");
      // Se c'e' una revisione IN CORSO si riprende da li', invece di
      // ripartire dalle aree arrivate dal backend.
      //
      // "In corso" vuol dire con almeno un riquadro. Una lista vuota non e' una
      // revisione: e' l'assenza di revisione, e succede subito dopo un
      // "Anonimizza" riuscito, quando i riquadri sono ormai impressi nel
      // documento. La copia autorevole in quel momento sta sul server (POST
      // /redact la scrive nel job e GET /editor la restituisce), quindi deve
      // vincere quella - prima la lista vuota la copriva e al rientro l'editor
      // mostrava l'originale pulito, come se il documento fosse stato rifatto.
      const ripresa = statoDaRipristinare(statoSalvatoRef.current, normalized);
      riferimentoRef.current = normalized;
      caricatoRef.current = true;
      setBboxes(ripresa.bboxes);
      setConfermati(new Set(ripresa.confermati));
      // Always fit-to-viewport on small screens (the 1.25 default overflows phones),
      // and for raster-like sources on any screen.
      if (needsFit || isMobileHeight()) {
        setPendingFitScale(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Caricamento editor non riuscito.");
      setPdf(null);
      setPdfBytes(null);
      setBboxes([]);
      setFileName(null);
    } finally {
      setLoading(false);
    }
  }, [jobId, fileIndex, sessionId]);

  useEffect(() => {
    loadFromApi();
  }, [loadFromApi]);

  useEffect(() => {
    if (!pdf || !pendingFitScale || loading) return undefined;

    let cancelled = false;
    let rafId = 0;

    const applyFitScale = async () => {
      const el = scrollRef.current;
      if (!el || el.clientWidth < 40) {
        rafId = requestAnimationFrame(applyFitScale);
        return;
      }
      try {
        const fit = await computeFitScaleForPage(pdf, pageNum, el);
        if (!cancelled) {
          setScale(Math.min(fit * 1.15, 2));
          setPendingFitScale(false);
        }
      } catch {
        if (!cancelled) setPendingFitScale(false);
      }
    };

    rafId = requestAnimationFrame(applyFitScale);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [pdf, pendingFitScale, loading]);

  const hasSelection = selectedIds.size > 0;

  // Terzo stato. Il contatore conta le segnalazioni ancora presenti
  // nell'editor: cosi' il numero coincide sempre con le etichette a schermo e
  // cala mentre l'utente le verifica, invece di restare un totale fisso.
  // L'unita' e' il gruppo di parole adiacenti, non la parola: un nome di due
  // parole e' una segnalazione sola, come lo e' per chi guarda la pagina.
  const tutteLeSegnalazioni = groupSospetti(bboxes);
  // Una segnalazione confermata resta oscurata ed esce dalla coda: il numero
  // in testa conta le cose ancora DA GUARDARE, non quelle da oscurare.
  const sospetti = tutteLeSegnalazioni.filter((r) => !confermati.has(r.id));
  // Le segnalazioni gia' guardate: restano oscurate, escono dalla coda e si
  // ritrovano in "Controllate", da cui si possono rimettere in coda. Prima
  // sparivano e basta, e dopo un "Anonimizza" ricomparivano tutte da
  // controllare, perche' il motore rimandava le aree senza memoria di chi le
  // aveva gia' viste (ora viaggiano con `controllato`, vedi handleRedact).
  const controllate = tutteLeSegnalazioni.filter((r) => confermati.has(r.id));
  const [vistaControllate, setVistaControllate] = useState(false);
  useEffect(() => {
    if (vistaControllate && controllate.length === 0) setVistaControllate(false);
  }, [vistaControllate, controllate.length]);

  // Selezionare tutto il gruppo permette di liberare "Mario Rossi" con un
  // Elimina solo, invece di una parola alla volta.
  const handleGoToSospetto = useCallback((run) => {
    setPageNum(run.page);
    setSelectedIds(new Set(run.items.map((b) => b.id)));
  }, []);

  // Il numero esatto lo conosce solo l'editor, che ha caricato il PDF e sa
  // raggruppare le parole. Lo comunica al chiamante perche' il selettore dei
  // documenti possa mostrarlo su quelli gia' aperti. Passa da una ref cosi'
  // l'effetto dipende dal solo conteggio: una callback ricreata a ogni render
  // del genitore lo farebbe scattare a vuoto in continuazione.
  const onSospettiChangeRef = useRef(onSospettiChange);
  onSospettiChangeRef.current = onSospettiChange;
  const onStatoChangeRef = useRef(onStatoChange);
  onStatoChangeRef.current = onStatoChange;

  // Il lavoro fatto su un documento sopravvive al passaggio a un altro tab o a
  // un altro file: in entrambi i casi il componente viene smontato, quindi lo
  // stato della revisione va tenuto fuori e restituito al rientro.
  //
  // La guardia e' sul CARICAMENTO riuscito, non sull'assenza di errori. Un
  // errore dell'anonimizzazione non deve fermare il salvataggio: prima, dopo un
  // /redact fallito, "error" restava valorizzato e da quel momento ogni
  // modifica successiva veniva persa - proprio quando l'utente sta rifacendo il
  // lavoro.
  useEffect(() => {
    if (!caricatoRef.current) return;
    onStatoChangeRef.current?.(statoDaSalvare(bboxes, confermati, !stesseAree(bboxes, riferimentoRef.current)));
  }, [bboxes, confermati]);
  useEffect(() => {
    if (!loading && !error) onSospettiChangeRef.current?.(sospetti.length);
  }, [sospetti.length, loading, error]);

  /** "Si'": la parola resta oscurata, la segnalazione esce dalla coda. */
  const handleConferma = useCallback((run) => {
    setConfermati((prev) => new Set(prev).add(run.id));
  }, []);

  /** "Riapri": la segnalazione torna in coda, senza toccare il documento. */
  const handleRiapri = useCallback((run) => {
    setConfermati((prev) => {
      const next = new Set(prev);
      next.delete(run.id);
      return next;
    });
  }, []);

  /** "No": la parola non va oscurata, le sue aree spariscono dal documento. */
  const handleLibera = useCallback((run) => {
    const ids = new Set(run.items.map((b) => b.id));
    setBboxes((prev) => prev.filter((b) => !ids.has(b.id)));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  // Solo nella direzione prudente. Il pulsante simmetrico - "lascia visibili
  // tutte" - non esiste di proposito: fra le parole segnalate una quota alta
  // e' PII vera, e un colpo solo che le libera tutte pubblicherebbe dati
  // personali con un clic. Confermare in blocco invece non cambia il
  // documento: dice soltanto "le ho guardate".
  const handleConfermaTutte = useCallback(() => {
    setConfermati((prev) => {
      const next = new Set(prev);
      groupSospetti(bboxes).forEach((r) => next.add(r.id));
      return next;
    });
  }, [bboxes]);

  const handleSelectBbox = useCallback((id, event) => {
    if (id == null) {
      setSelectedIds(new Set());
      return;
    }
    const additive = !!(event?.ctrlKey || event?.metaKey || event?.shiftKey);
    setSelectedIds((prev) => {
      if (!additive) return new Set([id]);
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleDeleteSelected = useCallback(() => {
    if (!selectedIds.size) return;
    const toRemove = selectedIds;
    setBboxes((prev) => prev.filter((b) => !toRemove.has(b.id)));
    setSelectedIds(new Set());
  }, [selectedIds]);

  useEffect(() => {
    if (!hasSelection || busy) return;
    const onKeyDown = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const target = e.target;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      e.preventDefault();
      handleDeleteSelected();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hasSelection, busy, handleDeleteSelected]);

  /**
   * @param {{restaNellEditor?: boolean}} [opzioni] restaNellEditor: la pagina
   *   non deve tornare all'anteprima, perche' seguono altri documenti.
   * @returns {Promise<boolean>} vero se il documento e' stato ridatto
   */
  const handleRedact = async (opzioni = {}) => {
    if (!pdf || !pdfBytes || bboxes.length === 0) return false;
    setRedacting(true);
    setError(null);
    try {
      // Le segnalazioni confermate partono con `controllato`: il motore le
      // rimanda cosi' com'erano, e alla riapertura non tornano in coda.
      const idsControllati = new Set(
        tutteLeSegnalazioni.filter((r) => confermati.has(r.id)).flatMap((r) => r.items.map((w) => w.id)),
      );
      const apiBboxes = await buildRedactPayload(pdf, bboxes, idsControllati);
      const redactedBytes = await redactPdf(jobId, fileIndex, apiBboxes, sessionId);
      const redactedName = nomeAnonimizzato(fileName);
      loadKeyRef.current = null;
      setPdfBytes(redactedBytes);
      setFileName(redactedName);
      // Le aree sono ormai impresse nel documento: niente piu' da applicare.
      riferimentoRef.current = [];
      setBboxes([]);
      setSelectedIds(new Set());
      setConfermati(new Set());
      const pdfjs = await loadPdfJs();
      const doc = await loadPdfDocument(pdfjs, redactedBytes);
      setPdf(doc);
      setPageNum(1);
      onRedactComplete?.(redactedBytes, redactedName, opzioni);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Anonimizzazione non riuscita.");
      return false;
    } finally {
      setRedacting(false);
    }
  };

  /** Prima il documento aperto, poi gli altri: la sequenza la guida la pagina. */
  const handleRedactAll = async () => {
    const fattoQui = bboxes.length ? await handleRedact({ restaNellEditor: true }) : false;
    await onRedactAll?.(fattoQui);
  };

  useEffect(() => {
    if (!pdf || !canvasRef.current) return;

    let cancelled = false;
    setRendering(true);

    (async () => {
      try {
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }

        const page = await pdf.getPage(pageNum);
        if (cancelled) return;

        const vp = page.getViewport({ scale });
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (!ctx) return;

        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.floor(vp.width * dpr);
        canvas.height = Math.floor(vp.height * dpr);
        canvas.style.width = `${vp.width}px`;
        canvas.style.height = `${vp.height}px`;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);

        const task = page.render({ canvasContext: ctx, viewport: vp });
        renderTaskRef.current = task;
        await task.promise;
        renderTaskRef.current = null;
        if (!cancelled) setViewport(vp);
      } catch (e) {
        if (e instanceof Error && /cancel/i.test(e.message)) return;
        if (!cancelled) setError(e instanceof Error ? e.message : "Rendering non riuscito.");
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();

    return () => {
      cancelled = true;
      setRendering(false);
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdf, pageNum, scale]);

  const totalPages = pdf?.numPages ?? 0;

  const redactOverlayDetail = bboxes.length > 0
    ? `${bboxes.length} ${bboxes.length === 1 ? "area in elaborazione" : "aree in elaborazione"}`
    : null;
  const viewerHeight = isMobileHeight() ? 480 : 720;
  // La sidebar sta a sinistra: row-reverse tiene il visore per primo nel
  // DOM - e quindi nell'ordine di lettura e di tabulazione - mettendolo pero'
  // a destra. Su schermo stretto la colonna toglierebbe al documento la
  // larghezza che gli serve, quindi scende sotto il visore.
  const compatto = isMobileHeight();

  return (
    <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 12 }}>
      <style>{`
        @keyframes obscura-editor-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes obscura-editor-progress {
          0% { width: 12%; margin-left: 0; }
          50% { width: 58%; margin-left: 22%; }
          100% { width: 12%; margin-left: 88%; }
        }
      `}</style>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 14px",
          background: EDITOR_C.white,
          border: `1px solid ${EDITOR_C.border}`,
          borderRadius: EDITOR_C.radius,
        }}
      >
        {totalPages > 0 && (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <EditorToolButton
                active={tool === "bbox"}
                disabled={busy}
                onClick={() => setTool("bbox")}
                label="Disegna area"
                hint="Traccia i riquadri"
                icon={(
                  <EditorIcon>
                    <rect x="4" y="4" width="14" height="14" rx="2" />
                    <line x1="12" y1="8" x2="12" y2="14" />
                    <line x1="9" y1="11" x2="15" y2="11" />
                  </EditorIcon>
                )}
              />
              <EditorToolButton
                active={tool === "pan"}
                disabled={busy}
                onClick={() => setTool("pan")}
                label="Seleziona"
                hint="Ctrl/Cmd/Shift per più aree"
                icon={(
                  <EditorIcon>
                    <path d="M5 4l6 14 2.5-5.5L19 10z" />
                  </EditorIcon>
                )}
              />
              <EditorToolButton
                active={false}
                disabled={busy || !hasSelection}
                onClick={handleDeleteSelected}
                label={hasSelection ? `Elimina (${selectedIds.size})` : "Elimina"}
                hint="Aree selezionate"
                icon={(
                  <EditorIcon>
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M8 6V4h8v2M19 6l-1 14H6L5 6" />
                  </EditorIcon>
                )}
              />
              {/* Le segnalazioni gia' viste, a destra di Elimina: un archivio
                  da consultare, e da cui rimetterne una in coda. */}
              {controllate.length > 0 && (
                <EditorToolButton
                  active={vistaControllate}
                  disabled={busy}
                  onClick={() => setVistaControllate((v) => !v)}
                  label={`Controllate (${controllate.length})`}
                  hint="Segnalazioni già viste"
                  icon={(
                    <EditorIcon>
                      <polyline points="20 6 9 17 4 12" />
                    </EditorIcon>
                  )}
                />
              )}
            </div>

            {sospetti.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "7px 12px",
                  borderRadius: 999,
                  background: EDITOR_C.sospettoBg,
                  border: `1px solid ${EDITOR_C.sospettoBorder}`,
                  fontFamily: EDITOR_F.body,
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#9A3412",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{ width: 9, height: 9, borderRadius: "50%", background: EDITOR_C.sospetto, flexShrink: 0 }}
                />
                {sospetti.length} da controllare
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginLeft: "auto" }}>
              <EditorControlGroup>
                <button type="button" style={editorControlBtnStyle(busy)} onClick={() => setScale((s) => Math.max(0.5, s - 0.25))} disabled={busy}>−</button>
                <span style={{ fontSize: 12, color: EDITOR_C.textNum, minWidth: 44, textAlign: "center", fontFamily: EDITOR_F.body, fontWeight: 600 }}>{Math.round(scale * 100)}%</span>
                <button type="button" style={editorControlBtnStyle(busy)} onClick={() => setScale((s) => Math.min(3, s + 0.25))} disabled={busy}>+</button>
              </EditorControlGroup>
              <EditorControlGroup>
                <button type="button" style={editorControlBtnStyle(pageNum <= 1 || busy)} onClick={() => setPageNum((p) => Math.max(1, p - 1))} disabled={pageNum <= 1 || busy}>←</button>
                <span style={{ fontSize: 12, color: EDITOR_C.textNum, fontFamily: EDITOR_F.body, fontWeight: 600, minWidth: 48, textAlign: "center" }}>
                  {pageNum} / {totalPages}
                </span>
                <button type="button" style={editorControlBtnStyle(pageNum >= totalPages || busy)} onClick={() => setPageNum((p) => Math.min(totalPages, p + 1))} disabled={pageNum >= totalPages || busy}>→</button>
              </EditorControlGroup>
            </div>
          </>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: compatto ? "column" : "row-reverse", gap: 12, alignItems: "flex-start" }}>
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          minWidth: 0,
          width: "100%",
          position: "relative",
          background: "#1a1a1a",
          borderRadius: EDITOR_C.radius,
          border: `1px solid ${EDITOR_C.border}`,
          minHeight: loading ? viewerHeight : 420,
          height: loading ? viewerHeight : undefined,
          maxHeight: viewerHeight,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          display: "flex",
          justifyContent: "safe center",
          alignItems: loading ? "stretch" : "flex-start",
          padding: loading ? 0 : 16,
        }}
      >
        {loading && (
          <EditorBusyOverlay
            title="Caricamento editor"
            subtitle="Stiamo preparando il documento originale e le aree suggerite."
          />
        )}
        {redacting && (
          <EditorBusyOverlay
            title="Anonimizzazione in corso"
            subtitle="OBSCURA sta oscurando le aree selezionate sul PDF."
            detail={redactOverlayDetail}
          />
        )}
        {error && !loading && !redacting && (
          <p style={{ color: "#f5a5a5", fontSize: 13, alignSelf: "center", textAlign: "center", padding: "0 16px", position: "relative", zIndex: 1 }}>
            {error}
          </p>
        )}
        {!loading && !error && pdf && (
          <div
            style={{
              position: "relative",
              width: viewport?.width ?? 1,
              height: viewport?.height ?? 1,
              flexShrink: 0,
              visibility: viewport ? "visible" : "hidden",
              opacity: redacting ? 0.35 : 1,
              transition: "opacity 0.25s ease",
              filter: redacting ? "blur(1px)" : "none",
            }}
          >
            <canvas ref={canvasRef} style={{ display: "block", background: "#fff" }} />
            {rendering && !loading && !redacting && (
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.45)",
                  pointerEvents: "none",
                }}
              >
                <span style={{ fontSize: 12, color: EDITOR_C.textNum, fontFamily: EDITOR_F.body, fontWeight: 600 }}>Rendering…</span>
              </div>
            )}
            {viewport && (
              <PdfOverlay
                viewport={viewport}
                pageNum={pageNum}
                bboxes={bboxes}
                selectedIds={selectedIds}
                tool={tool}
                onAddBbox={(bbox) => setBboxes((prev) => [...prev, bbox])}
                onSelectBbox={handleSelectBbox}
                confermati={confermati}
              />
            )}
          </div>
        )}
      </div>

      {(sospetti.length > 0 || (vistaControllate && controllate.length > 0)) && !loading && !error && (
        <aside
          aria-label={vistaControllate ? "Aree controllate" : "Aree da controllare"}
          style={{
            width: compatto ? "100%" : 300,
            flexShrink: 0,
            boxSizing: "border-box",
            maxHeight: compatto ? undefined : viewerHeight,
            overflowY: compatto ? undefined : "auto",
            padding: "14px 16px",
            background: EDITOR_C.white,
            border: `1px solid ${EDITOR_C.border}`,
            borderRadius: EDITOR_C.radius,
          }}
        >
          {controllate.length > 0 && (
            <div role="tablist" aria-label="Segnalazioni" style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              {[
                { fatte: false, testo: `Da controllare (${sospetti.length})`, colore: EDITOR_C.sospetto },
                { fatte: true, testo: `Controllate (${controllate.length})`, colore: EDITOR_C.controllata },
              ].map((t) => {
                const attivo = t.fatte === vistaControllate;
                return (
                  <button
                    key={t.testo}
                    type="button"
                    role="tab"
                    aria-selected={attivo}
                    onClick={() => setVistaControllate(t.fatte)}
                    style={{ flex: 1, height: 30, borderRadius: 8, border: `1px solid ${attivo ? t.colore : EDITOR_C.border}`, background: attivo ? EDITOR_C.white : "transparent", color: attivo ? t.colore : EDITOR_C.textNum, fontSize: 11, fontWeight: 700, fontFamily: EDITOR_F.body, cursor: "pointer" }}
                  >
                    {t.testo}
                  </button>
                );
              })}
            </div>
          )}
          {vistaControllate ? (
            <>
              <p style={{ margin: "0 0 12px", fontSize: 12, color: EDITOR_C.textNum, fontFamily: EDITOR_F.body, lineHeight: 1.45 }}>
                Le hai gi&agrave; guardate: restano oscurate. &laquo;Riapri&raquo; ne rimette una in coda.
              </p>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {controllate.map((b) => {
                  const attiva = b.items.some((w) => selectedIds.has(w.id));
                  const parola = b.items.map((w) => w.label).filter(Boolean).join(" ");
                  return (
                    <li key={b.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => handleGoToSospetto(b)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleGoToSospetto(b); } }}
                        style={{ padding: "10px 12px", borderRadius: 8, cursor: "pointer", background: attiva ? EDITOR_C.smartBlue20 : EDITOR_C.white, border: `1px solid ${attiva ? EDITOR_C.smartBlue : EDITOR_C.border}` }}
                      >
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", color: EDITOR_C.controllata, fontFamily: EDITOR_F.body }}>
                            {(b.categoria || "ALTRO").toUpperCase()}
                          </span>
                          <span style={{ fontSize: 11, color: EDITOR_C.textNum, fontFamily: EDITOR_F.body, marginLeft: "auto" }}>p. {b.page}</span>
                        </div>
                        {parola && (
                          <p title={parola} style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600, color: EDITOR_C.textTitle, fontFamily: EDITOR_F.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {parola}
                          </p>
                        )}
                        <div style={{ display: "flex", gap: 6 }}>
                          <button type="button" onClick={(e) => { e.stopPropagation(); handleGoToSospetto(b); }} disabled={busy} style={azioneStyle(busy, "vai")}>Vai</button>
                          <button type="button" onClick={(e) => { e.stopPropagation(); handleRiapri(b); }} disabled={busy} style={azioneStyle(busy)} title="Rimetti in coda">Riapri</button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: EDITOR_C.sospetto, fontFamily: EDITOR_F.display }}>
              {sospetti.length}
            </span>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: EDITOR_C.textTitle, fontFamily: EDITOR_F.display }}>
              da controllare
            </h4>
          </div>
          <p style={{ margin: "8px 0 12px", fontSize: 12, color: EDITOR_C.textNum, fontFamily: EDITOR_F.body, lineHeight: 1.45 }}>
            Restano tutte oscurate. Guardale una per una: decidi tu quali lasciare cosi&apos; e quali no.
          </p>

          {/* Solo nella direzione prudente: conferma in blocco non cambia il
              documento. Il gemello "lascia visibili tutte" non c'e' apposta. */}
          <button
            type="button"
            onClick={handleConfermaTutte}
            disabled={busy}
            style={{
              width: "100%",
              height: 34,
              marginBottom: 12,
              borderRadius: 8,
              border: `1px solid ${EDITOR_C.border}`,
              background: EDITOR_C.white,
              color: EDITOR_C.textTitle,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: EDITOR_F.body,
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.5 : 1,
            }}
          >
            Oscura tutte ({sospetti.length})
          </button>

          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {sospetti.map((b) => {
              const attiva = b.items.some((w) => selectedIds.has(w.id));
              // La parola segnalata: e' il dato che fa decidere. La categoria
              // dice di che tipo e', ma "Sara" o "Molinette" dice se e' una
              // persona vera o il nome di un ospedale.
              const parola = b.items.map((w) => w.label).filter(Boolean).join(" ");
              return (
                <li key={b.id}>
                  {/* Tutta la scheda porta alla parola: cliccarla equivale a
                      premere Vai, e i tre pulsanti restano per le azioni. */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleGoToSospetto(b)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleGoToSospetto(b); } }}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 8,
                      cursor: "pointer",
                      // Le schede non correnti sono piu' smorte: cosi' quella
                      // selezionata stacca invece di confondersi nella fila.
                      background: attiva ? EDITOR_C.sospettoBg : "#FFFBF5",
                      border: `1px solid ${attiva ? EDITOR_C.sospetto : "#FDE9D2"}`,
                      opacity: attiva ? 1 : 0.82,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", color: attiva ? "#9A3412" : "#B45309", fontFamily: EDITOR_F.body }}>
                        {(b.categoria || "ALTRO").toUpperCase()}
                      </span>
                      <span style={{ fontSize: 11, color: EDITOR_C.textNum, fontFamily: EDITOR_F.body, marginLeft: "auto" }}>
                        p. {b.page}
                      </span>
                    </div>
                    {parola && (
                      <p
                        title={parola}
                        style={{
                          margin: "0 0 8px",
                          fontSize: 13,
                          fontWeight: 600,
                          color: EDITOR_C.textTitle,
                          fontFamily: EDITOR_F.body,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {parola}
                      </p>
                    )}
                    <div style={{ display: "flex", gap: 6 }}>
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleGoToSospetto(b); }} disabled={busy}
                              style={azioneStyle(busy, "vai")}>Vai</button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleConferma(b); }} disabled={busy}
                              style={azioneStyle(busy, "si")}
                              title="Resta oscurata">Oscura</button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleLibera(b); }} disabled={busy}
                              style={azioneStyle(busy, "no")}
                              title="Non oscurare questa area">Lascia</button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
            </>
          )}
        </aside>
      )}
      </div>

      {totalPages > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
            padding: "14px 16px",
            background: EDITOR_C.white,
            border: `1px solid ${EDITOR_C.border}`,
            borderRadius: EDITOR_C.radius,
          }}
        >
          <p style={{ margin: 0, fontSize: 13, color: EDITOR_C.textNum, fontFamily: EDITOR_F.body, lineHeight: 1.45 }}>
            {altriDaApplicare > 0
              ? `Ci sono modifiche anche su ${altriDaApplicare === 1 ? "un altro documento" : `altri ${altriDaApplicare} documenti`}: "Anonimizza tutti" le applica una dopo l'altra`
              : "Oscura le aree e prepara il download"}
          </p>
          {(() => {
            const tutti = altriDaApplicare > 0;
            const totale = altriDaApplicare + (bboxes.length ? 1 : 0);
            const disabilitato = busy || (bboxes.length === 0 && !tutti);
            const etichetta = redacting ? "Anonimizzazione…"
              : bloccato ? "Anonimizzazione degli altri documenti…"
                : tutti ? `Anonimizza tutti (${totale})` : "Anonimizza";
            return (
              <button
                type="button"
                style={{
                  height: 42,
                  padding: "0 28px",
                  borderRadius: 8,
                  border: "none",
                  background: EDITOR_C.btnBg,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: EDITOR_F.body,
                  cursor: disabilitato ? "not-allowed" : "pointer",
                  opacity: disabilitato ? 0.5 : 1,
                }}
                onClick={tutti ? handleRedactAll : () => handleRedact()}
                disabled={disabilitato}
              >
                {etichetta}
              </button>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function isMobileHeight() {
  return typeof window !== "undefined" && window.innerWidth < 768;
}
